import { useEffect, useRef, useState, type ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { DetectionBox } from '../components/installer/DetectionBox.js'
import { Icon } from '../components/installer/icons.js'
import { ANCHOR_FAIL, PROOF_STEPS } from '../lib/proofSteps.js'
import { ElectricalCaptureScreen } from './ElectricalCaptureScreen.js'
import { useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

/**
 * Scripted camera viewfinder for a single proof step. There is no real camera
 * and no AI: a primer overlay, an AR-lock simulation, an analyzing overlay, and
 * a flash are all driven by `setTimeout`s over a pre-baked photo with an
 * app-rendered bounding box. The signature beat is the anchor fail path:
 * the first `anchor` attempt routes to `verify-failed`; the retake passes.
 *
 * Timer cleanup is load-bearing — see the unmount effect below.
 */
export const CaptureScreen = (): ReactElement | null => {
  const { state, dispatch } = useDemoStore()
  const { goto } = useStep()

  // Local UI state — deliberately NOT in the store. A fresh mount (the router
  // unmounts CaptureScreen between photos) resets these via the initializers,
  // so there is no cross-photo reset effect.
  const [primerVisible, setPrimerVisible] = useState(true)
  const [arLocked, setArLocked] = useState(false)
  const [framingPromptText, setFramingPromptText] = useState('Hold steady · aligning')
  const [flashing, setFlashing] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)

  // All pending timers live in refs so the unmount cleanup can clear every one.
  // If any timer is still pending when the component unmounts (user taps Back
  // mid-sequence), clearing it here guarantees its callback never runs — so no
  // local `setState` ever fires after unmount.
  const lockTimerRef = useRef<number | null>(null)
  const autoCaptureTimerRef = useRef<number | null>(null)
  const flashOffTimerRef = useRef<number | null>(null)
  const analyzeOnTimerRef = useRef<number | null>(null)
  const resolveTimerRef = useRef<number | null>(null)

  // The viewfinder <video> for video-backed shots. The AR box is BURNED into the
  // clip, so nothing is positioned at runtime; playback starts on primer dismissal
  // (a tap, which satisfies mobile autoplay-gesture rules).
  const videoRef = useRef<HTMLVideoElement>(null)

  // Guard against double-tap: set to true synchronously when capturePhoto
  // begins, so a second tap within the 200ms pre-analyzing window is a no-op.
  // No reset needed — a successful capture navigates away and unmounts; a fresh
  // mount (or a clean activeIdx change) gets a fresh ref value of false.
  const capturingRef = useRef<boolean>(false)

  useEffect(() => {
    return () => {
      for (const ref of [
        lockTimerRef,
        autoCaptureTimerRef,
        flashOffTimerRef,
        analyzeOnTimerRef,
        resolveTimerRef,
      ]) {
        if (ref.current !== null) {
          clearTimeout(ref.current)
          ref.current = null
        }
      }
    }
  }, [])

  // Reset local camera state whenever the active step index changes. This makes
  // the screen correct whether or not the router remounts it between photos.
  // Idempotent on a fresh mount (state already matches the initializers).
  useEffect(() => {
    setPrimerVisible(true)
    setArLocked(false)
    setFramingPromptText('Hold steady · aligning')
    setFlashing(false)
    setAnalyzing(false)
    capturingRef.current = false
  }, [state.activeIdx])

  // ── All hooks are above this guard. ──────────────────────────────────────
  const activeStep = PROOF_STEPS[state.activeIdx]
  if (activeStep === undefined) return null

  // Multi-capture checkpoints (electrical) run their own 3-shot viewfinder.
  if (activeStep.subShots !== undefined) return <ElectricalCaptureScreen step={activeStep} />

  const showAnchorFail = activeStep.id === 'anchor' && !state.anchorAttempted
  const cameraImage = showAnchorFail ? ANCHOR_FAIL.photo : activeStep.basePhoto
  const cameraBoxSpec = showAnchorFail ? ANCHOR_FAIL.cameraBox : activeStep.cameraBox[0]
  const activeVideo = showAnchorFail ? ANCHOR_FAIL.video : activeStep.video
  const arVariant = arLocked ? (showAnchorFail ? 'fail' : 'locked') : 'scanning'

  const dismissPrimer = (): void => {
    setPrimerVisible(false)
    // Start the clip on the dismiss tap (satisfies mobile autoplay-gesture rules).
    // No-op for still shots (videoRef is unattached); rejection is swallowed.
    videoRef.current?.play().catch(() => {})
    if (lockTimerRef.current !== null) clearTimeout(lockTimerRef.current)
    lockTimerRef.current = window.setTimeout(() => {
      setArLocked(true)
      // Video shots capture themselves: the clip is a scripted, hands-free beat, so
      // after the lock we fire the shutter automatically (a still shot still waits for
      // the installer's tap). The delay lets the locked box register AND the clip play
      // out a bit — ~3.8s total on screen (1.2s to lock + 2.6s here) before the flash.
      if (activeVideo !== undefined) {
        setFramingPromptText('Framing locked · capturing…')
        autoCaptureTimerRef.current = window.setTimeout(runCapture, 2600)
      } else {
        setFramingPromptText('Framing locked · tap to capture')
      }
    }, 1200)
  }

  // The capture sequence itself, with no lock check — callable from the shutter tap
  // (via capturePhoto, which gates on arLocked) and from the auto-capture timer (which
  // only fires after the lock, so the check would be redundant there).
  const runCapture = (): void => {
    if (analyzing || capturingRef.current) return
    capturingRef.current = true
    setFlashing(true)
    flashOffTimerRef.current = window.setTimeout(() => setFlashing(false), 120)
    analyzeOnTimerRef.current = window.setTimeout(() => setAnalyzing(true), 200)
    resolveTimerRef.current = window.setTimeout(() => {
      setAnalyzing(false)
      // dispatch + goto fire unconditionally: the store and the URL survive
      // unmount, so there is no React state to corrupt. (And in practice this
      // callback cannot run post-unmount anyway — the unmount cleanup clears
      // this timer first.)
      if (activeStep.id === 'anchor' && !state.anchorAttempted) {
        dispatch({ type: 'ANCHOR_ATTEMPTED' })
        goto('verify-failed')
      } else {
        dispatch({ type: 'MARK_CAPTURED', id: activeStep.id })
        goto('verify-passed')
      }
    }, 1800)
  }

  // Shutter tap. Ignored until the AR box locks; a video shot usually auto-captures
  // first (capturingRef then makes this a no-op), but a manual tap still works.
  const capturePhoto = (): void => {
    if (!arLocked) return
    runCapture()
  }

  return (
    <AppShell fullbleed>
      {/* installer-surface must be an ANCESTOR of .camera-screen — every installer.css
          rule is descendant-scoped (`.installer-surface .camera-screen`), so putting both
          classes on one element silently drops the camera's entire flex layout (body
          collapses to height 0). The wrapper fills the fullbleed (relative) main. */}
      <div className="installer-surface" style={{ position: 'absolute', inset: 0 }}>
        <div className={`camera-screen ${primerVisible ? 'primer-active' : ''}`}>
        <div className="camera-header">
          <button className="back" onClick={() => goto('proof-bundle')} aria-label="Back">
            <Icon.Back />
          </button>
          <div className="title">{activeStep.label}</div>
          <div className="privacy-badge">
            <Icon.ShieldPlain size={12} />
            Privacy mask on
          </div>
        </div>

        <div className="camera-body">
          {activeVideo !== undefined ? (
            <video
              ref={videoRef}
              className="camera-bg camera-bg-video"
              src={activeVideo.src}
              poster={activeVideo.poster}
              muted
              playsInline
              loop
              preload="auto"
            />
          ) : (
            <div className="camera-bg" style={{ backgroundImage: `url(${cameraImage})` }} />
          )}

          {/* Primer overlay */}
          <div className={`primer-overlay ${primerVisible ? 'visible' : ''}`}>
            <div className="primer-card">
              <div className="primer-preview">
                {/* INSTRUCTIONAL guide, not the result: a clean 3:2 landscape framing example
                    (never the clip's burned-box poster — that would spoil the run) with the
                    app-drawn dashed framing guide + plain-language caption. Rendered for EVERY
                    step kind, video and still alike. Falls back to basePhoto defensively. */}
                <div
                  className="primer-preview-scene"
                  style={{
                    backgroundImage: `url(${activeStep.primerImage || activeStep.basePhoto})`,
                    backgroundPosition: 'center',
                  }}
                >
                  {/* Framing guide only — no caption chip: a label over the box hides
                      exactly what the guide is pointing at (07-19 review). */}
                  {activeStep.primerBox.map((box) => (
                    <div
                      key={`${box.text}-${box.top}-${box.left}`}
                      className="primer-guide-box"
                      style={{
                        top: box.top,
                        left: box.left,
                        width: box.width,
                        height: box.height,
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="primer-title">{activeStep.label}</div>
              <div className="primer-section-label">WHAT TO CAPTURE</div>
              <ul className="primer-list">
                {activeStep.capture.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="primer-why">
                <div className="primer-why-label">WHY THIS MATTERS</div>
                <div className="primer-why-body">{activeStep.why}</div>
              </div>
              <button className="primer-btn" onClick={dismissPrimer}>
                <Icon.Camera /> Ready to capture
              </button>
            </div>
          </div>

          {/* AR box — burned into the clip for video shots; app-rendered for stills */}
          {activeVideo === undefined && cameraBoxSpec !== undefined && (
            <DetectionBox box={cameraBoxSpec} variant={arVariant} />
          )}

          {/* Framing prompt */}
          <div className={`framing-prompt ${arLocked ? 'locked' : ''}`}>
            <Icon.Clock size={14} />
            <span>{framingPromptText}</span>
          </div>

          {/* Analyzing overlay */}
          <div className={`analyzing ${analyzing ? 'visible' : ''}`}>
            <div className="analyzing-spinner" />
            <div className="analyzing-text">Analyzing…</div>
            <div className="analyzing-sub">Running {activeStep.label.toLowerCase()} check</div>
          </div>

          {/* Flash */}
          <div className={`camera-capturing ${flashing ? 'flash' : ''}`} />
        </div>

        <div className="camera-controls">
          <div className="flashlight">
            <Icon.Flash />
          </div>
          <div className="camera-shutter" onClick={capturePhoto}>
            <div className="camera-shutter-inner">
              <Icon.Camera size={22} />
            </div>
          </div>
          <div style={{ width: 36, height: 36 }} />
        </div>
        </div>
      </div>
    </AppShell>
  )
}
